import type { PresenceUser } from "../types";
import type { SDef } from "./model";

const COLORS = ["#2563eb", "#db2777", "#059669", "#d97706", "#7c3aed", "#0891b2", "#dc2626", "#4f46e5"];

function colorOf(userId: string): string {
  let h = 0;
  for (let i = 0; i < userId.length; i++) h = (h * 31 + userId.charCodeAt(i)) >>> 0;
  return COLORS[h % COLORS.length];
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "?") + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}

/** Who else has this report open right now (like the avatars in Word/Excel online). */
export function PresenceBar({ peers, def, note }: { peers: PresenceUser[]; def: SDef | null; note?: string }) {
  const others = peers.filter((p) => !p.is_me);
  if (others.length === 0 && !note) return null;
  const myId = peers.find((x) => x.is_me)?.user_id;
  const where = (p: PresenceUser): string => {
    const page = def?.pages.find((x) => x.id === p.page_id);
    const widget = page?.widgets.find((w) => w.id === p.widget_id);
    const bits = [p.state === "editing" ? "editing" : "viewing"];
    if (widget) bits.push(`"${widget.title || widget.type}"`);
    if (page) bits.push(`on ${page.name}`);
    return bits.join(" ");
  };
  return (
    <div className="st-presence" aria-label="People in this report">
      {note ? <span className="st-presence__note">{note}</span> : null}
      {others.slice(0, 5).map((p) => (
        <span
          key={`${p.user_id}:${p.client_id}`}
          className={`st-presence__av${p.state === "editing" ? " is-editing" : ""}`}
          style={{ background: colorOf(p.user_id) }}
          title={`${p.name}${p.user_id === myId ? " (you, another tab)" : ""} - ${where(p)}`}
        >
          {initials(p.name)}
        </span>
      ))}
      {others.length > 5 ? <span className="st-presence__more">+{others.length - 5}</span> : null}
      {others.length > 0 ? <span className="st-presence__count">{others.length} other{others.length === 1 ? "" : "s"} here</span> : null}
    </div>
  );
}
